import React, { useState, useEffect, useCallback } from 'react';
import { tripsAPI } from '../utils/api';
import MapComponent from './MapComponent';
import PostDetailModal from './PostDetailModal';
import './TripDetail.css';

const TripDetail = ({ tripId, onBack }) => {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'

  const loadTrip = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tripsAPI.getTripById(tripId);
      setTrip(response.data);
    } catch (err) {
      console.error('Error loading trip:', err);
      setError('Failed to load trip details');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    loadTrip();
  }, [loadTrip]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1 ? '1 day' : `${diffDays} days`;
  };

  // Extract posts with location data for map
  const getPostsForMap = () => {
    if (!trip || !trip.posts) return [];
    
    return trip.posts
      .filter(tp => tp.post && tp.post.campingSpotId)
      .map(tp => ({
        ...tp.post,
        order: tp.order,
        tripPostId: tp.id
      }));
  };

  if (loading) {
    return (
      <div className="trip-detail loading">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="loading-spinner">Loading trip details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trip-detail error">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="error-message">{error}</div>
        <button onClick={loadTrip} className="retry-button">Retry</button>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="trip-detail error">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="error-message">Trip not found</div>
      </div>
    );
  }

  const postsForMap = getPostsForMap();

  return (
    <div className="trip-detail">
      {/* Header */}
      <div className="trip-header">
        <button onClick={onBack} className="back-button">← Back</button>
        
        <div className="trip-header-content">
          {trip.coverImage && (
            <div className="trip-header-cover">
              <img src={trip.coverImage} alt={trip.title} />
            </div>
          )}
          
          <div className="trip-header-info">
            <h1 className="trip-title">{trip.title}</h1>
            
            {trip.description && (
              <p className="trip-description">{trip.description}</p>
            )}
            
            <div className="trip-meta">
              <span className="trip-dates">
                {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
              </span>
              <span className="trip-duration">
                {getDuration(trip.startDate, trip.endDate)}
              </span>
              <span className="trip-post-count">
                {trip.postCount} {trip.postCount === 1 ? 'post' : 'posts'}
              </span>
            </div>

            {trip.user && (
              <div className="trip-author">
                {trip.user.avatar && (
                  <img 
                    src={trip.user.avatar} 
                    alt={trip.user.fullName}
                    className="author-avatar"
                  />
                )}
                <span className="author-name">{trip.user.fullName}</span>
              </div>
            )}
          </div>
        </div>

        {/* View mode toggle */}
        <div className="view-mode-toggle">
          <button 
            className={`mode-button ${viewMode === 'map' ? 'active' : ''}`}
            onClick={() => setViewMode('map')}
          >
            🗺️ Map View
          </button>
          <button 
            className={`mode-button ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            📋 List View
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="trip-content">
        {viewMode === 'map' ? (
          <div className="trip-map-view">
            {postsForMap.length > 0 ? (
              <MapComponent 
                posts={postsForMap}
                onSelectPost={setSelectedPost}
                showTripRoute={true}
              />
            ) : (
              <div className="empty-map">
                <p>No posts with location data to display for this trip.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="trip-list-view">
            {trip.posts && trip.posts.length > 0 ? (
              <div className="trip-posts-list">
                {trip.posts
                  .sort((a, b) => a.order - b.order)
                  .map(tripPost => (
                    <div 
                      key={tripPost.id}
                      className="trip-post-item"
                      onClick={() => setSelectedPost(tripPost.post)}
                    >
                      <div className="post-order-badge">{tripPost.order + 1}</div>
                      
                      {tripPost.post && (
                        <>
                          <h3 className="post-title">
                            {tripPost.post.content?.substring(0, 100) || 'No content'}
                          </h3>
                          
                          {tripPost.post.media && tripPost.post.media.length > 0 && (
                            <div className="post-media-preview">
                              {tripPost.post.media.slice(0, 3).map((media, idx) => (
                                <img 
                                  key={media.id}
                                  src={media.url}
                                  alt={`Media ${idx + 1}`}
                                  className="media-thumbnail"
                                />
                              ))}
                              {tripPost.post.media.length > 3 && (
                                <span className="more-media">
                                  +{tripPost.post.media.length - 3}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="post-meta">
                            <span className="post-author">
                              {tripPost.post.userName || 'Unknown'}
                            </span>
                            <span className="post-date">
                              {new Date(tripPost.post.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-posts">
                <p>No posts in this trip yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post Detail Modal */}
      {selectedPost && (
        <PostDetailModal 
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
        />
      )}
    </div>
  );
};

export default TripDetail;
